import { getApiClient } from '@src/shared/types/api-client'
import type {
  ContainerAnonymizationDashboard,
  ContainerStatus,
  DatabasePendingApprovalCount,
  ToggleDisabledRequest,
} from '@src/tools/anonymizer/types/Constants'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { anonymizerKeys } from './query-keys'

const getDatabasePendingApprovalCounts = async (): Promise<DatabasePendingApprovalCount[]> => {
  const apiClient = getApiClient()
  const response = await apiClient.get<DatabasePendingApprovalCount[]>('databases/pendingApprovalCounts')
  return response.data
}

export function useDatabasePendingApprovalCounts(): [DatabasePendingApprovalCount[], boolean] {
  const { data, isFetching } = useQuery({
    queryKey: anonymizerKeys.databasePendingApprovalCounts(),
    queryFn: getDatabasePendingApprovalCounts,
    placeholderData: [],
  })
  return [data ?? [], isFetching]
}

const getContainerStatuses = async (): Promise<ContainerStatus[]> => {
  const apiClient = getApiClient()
  const response = await apiClient.get<ContainerStatus[]>('databases/containers/statuses')
  return response.data
}

export function useContainerStatuses(): [ContainerStatus[], boolean] {
  const { data, isFetching } = useQuery({
    queryKey: anonymizerKeys.containerStatuses(),
    queryFn: getContainerStatuses,
    placeholderData: [],
  })
  return [data ?? [], isFetching]
}

export function useAnonymizationDashboard(dbName: string | null): [ContainerAnonymizationDashboard[], boolean] {
  const { data, isFetching } = useQuery({
    queryKey: anonymizerKeys.anonymizationDashboard(dbName!),
    queryFn: async () => {
      const apiClient = getApiClient()
      const response = await apiClient.get<ContainerAnonymizationDashboard[]>(
        `databases/${encodeURIComponent(dbName!)}/anonymization-dashboard`,
      )
      return response.data
    },
    enabled: !!dbName,
    placeholderData: [],
  })
  return [data ?? [], isFetching]
}

function patchDashboard(
  prev: ContainerAnonymizationDashboard[] | undefined,
  update: ContainerStatus,
): ContainerAnonymizationDashboard[] {
  if (!prev) return []
  return prev.map((s) =>
    s.dbName === update.dbName && s.containerName === update.containerName
      ? { ...s, isBlocked: update.isBlocked, isDisabled: update.isDisabled, disabledBy: update.disabledBy, disabledReason: update.disabledReason }
      : s,
  )
}

export function useToggleContainerDisabled() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (params: { dbName: string; containerName: string; body: ToggleDisabledRequest }) => {
      const apiClient = getApiClient()
      const response = await apiClient.put<ContainerStatus>(
        `databases/${encodeURIComponent(params.dbName)}/containers/${encodeURIComponent(params.containerName)}/disabled`,
        params.body,
      )
      return response.data
    },
    onSuccess: async (data) => {
      queryClient.setQueriesData<ContainerAnonymizationDashboard[]>(
        { queryKey: anonymizerKeys.allAnonymizationDashboard() },
        (prev) => patchDashboard(prev, data),
      )
      await queryClient.invalidateQueries({ queryKey: anonymizerKeys.containerStatuses() })
      await queryClient.invalidateQueries({ queryKey: anonymizerKeys.allContainers() })
      await queryClient.invalidateQueries({ queryKey: anonymizerKeys.databasePendingApprovalCounts() })
    },
  })
}

export function useTerminateContainerOrchestration() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (params: { dbName: string; containerName: string }) => {
      const apiClient = getApiClient()
      const response = await apiClient.post<{ terminated: number }>(
        `databases/${encodeURIComponent(params.dbName)}/containers/${encodeURIComponent(params.containerName)}/terminate-orchestration`,
      )
      return response.data
    },
    onSuccess: async (_data, params) => {
      await queryClient.invalidateQueries({ queryKey: anonymizerKeys.anonymizationDashboard(params.dbName) })
    },
  })
}

export function useUnblockContainer() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (params: { dbName: string; containerName: string }) => {
      const apiClient = getApiClient()
      const response = await apiClient.put<ContainerStatus>(
        `databases/${encodeURIComponent(params.dbName)}/containers/${encodeURIComponent(params.containerName)}/unblock`,
      )
      return response.data
    },
    onSuccess: async (data) => {
      queryClient.setQueriesData<ContainerAnonymizationDashboard[]>(
        { queryKey: anonymizerKeys.allAnonymizationDashboard() },
        (prev) => patchDashboard(prev, data),
      )
      await queryClient.invalidateQueries({ queryKey: anonymizerKeys.containerStatuses() })
      await queryClient.invalidateQueries({ queryKey: anonymizerKeys.allContainers() })
      await queryClient.invalidateQueries({ queryKey: anonymizerKeys.databasePendingApprovalCounts() })
    },
  })
}

export function useStartContainerAnonymization() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (params: { dbName: string; containerName: string }) => {
      const apiClient = getApiClient()
      const response = await apiClient.post<{ triggered: boolean }>(
        `databases/${encodeURIComponent(params.dbName)}/containers/${encodeURIComponent(params.containerName)}/start-anonymization`,
      )
      return response.data
    },
    onSuccess: async (_data, params) => {
      await queryClient.invalidateQueries({ queryKey: anonymizerKeys.anonymizationDashboard(params.dbName) })
    },
  })
}

export function useResetContainerAnonymization() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (params: { dbName: string; containerName: string }) => {
      const apiClient = getApiClient()
      const response = await apiClient.post<{ reset: boolean }>(
        `databases/${encodeURIComponent(params.dbName)}/containers/${encodeURIComponent(params.containerName)}/reset-anonymization`,
      )
      return response.data
    },
    onSuccess: async (_data, params) => {
      await queryClient.invalidateQueries({ queryKey: anonymizerKeys.anonymizationDashboard(params.dbName) })
    },
  })
}
